import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/ui/empty-state';
import { JackpotCard } from '@/components/jackpots/JackpotCard';
import {
  RodeoCalendarView,
  type CalendarRodeoUserEvent,
} from '@/components/rodeos/RodeoCalendarView';
import { useJackpotsList } from '@/api/extensions/useJackpotOps';
import { useMyRegistrations } from '@/api/extensions/useRegistrationOps';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import { isScheduleItemPast } from '@/utils/timezone';

type TabValue = 'upcoming' | 'past';
const PAGE_SIZE = 12;

function categorize(jackpots: any[]): Record<TabValue, any[]> {
  const result: Record<TabValue, any[]> = { upcoming: [], past: [] };

  // A jackpot is "past" only once today has moved beyond its last running
  // day IN THE JACKPOT'S OWN TIMEZONE — one still running tonight stays
  // "upcoming" through 11:59pm local and only flips to past at midnight there.
  // Anything scheduled for the future is "upcoming".
  for (const j of jackpots) {
    if (
      isScheduleItemPast({
        startDate: j.start_date,
        endDate: j.end_date,
        timezone: j.timezone,
      })
    ) {
      result.past.push(j);
    } else {
      result.upcoming.push(j);
    }
  }

  // Sort: upcoming ascending (soonest/in-progress first), past descending
  // (most recent first). Jackpots missing a start_date sink to the bottom.
  const startKey = (j: any) =>
    typeof j.start_date === 'string' ? j.start_date.substring(0, 10) : '';
  const sortByStart = (dir: 'asc' | 'desc') => (a: any, b: any) => {
    const ka = startKey(a);
    const kb = startKey(b);
    if (!ka && !kb) return 0;
    if (!ka) return 1;
    if (!kb) return -1;
    const cmp = ka.localeCompare(kb);
    return dir === 'asc' ? cmp : -cmp;
  };
  result.upcoming.sort(sortByStart('asc'));
  result.past.sort(sortByStart('desc'));

  return result;
}

export default function JackpotsPage() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.role?.role_name === 'admin');

  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const stored = sessionStorage.getItem('jackpots-tab');
    // Migrate legacy 'current' value (tab was removed) → 'upcoming'.
    if (stored === 'upcoming' || stored === 'past') return stored;
    return 'upcoming';
  });
  const [showCalendar, setShowCalendar] = useState<boolean>(() => {
    try {
      return localStorage.getItem('jackpots-show-calendar') === 'true';
    } catch {
      return false;
    }
  });
  const [pages, setPages] = useState<Record<TabValue, number>>({
    upcoming: 1,
    past: 1,
  });

  const handleShowCalendarChange = (next: boolean) => {
    setShowCalendar(next);
    try {
      localStorage.setItem('jackpots-show-calendar', String(next));
    } catch {
      // no-op: persistence is best-effort
    }
  };

  const { data, isLoading } = useJackpotsList({ is_published: true, pageSize: 200 });
  const userProfileId = useAuthStore((s) => s.profile?.id ?? null);
  const { data: myRegistrations } = useMyRegistrations({
    include_buddy_group: true,
  });
  // The /jackpot-ops/list endpoint selects rodeos.* without joining
  // organizations, so the payload only carries primary_organization_id;
  // we hydrate the host org name client-side.
  const { data: myOrganizations } = useOrganizations();
  const allJackpots = data?.data ?? [];

  const categorized = useMemo(() => categorize(allJackpots), [allJackpots]);

  // Calendar widget: the "upcoming" bucket now includes any jackpot whose
  // last day is today or later (in-progress events stay in upcoming until
  // their final day passes), so feeding it alone covers everything the
  // calendar should highlight. Resolve org name from the user's org list
  // and annotate each jackpot with the user's own registered events so the
  // day-popover can render "Your events" chips.
  const calendarJackpots = useMemo(() => {
    const list = [...categorized.upcoming];
    const orgNameById = new Map<string, string>();
    for (const o of (myOrganizations ?? []) as Array<{
      id?: string;
      name?: string;
    }>) {
      if (o?.id && o?.name) orgNameById.set(o.id, o.name);
    }
    const ownRegByEventId = new Map<
      string,
      { position?: string | null }
    >();
    if (userProfileId && myRegistrations) {
      for (const reg of myRegistrations as Array<{
        event_id?: string;
        user_profile_id?: string;
        position?: string | null;
        trig_status_name?: string;
      }>) {
        if (!reg.event_id) continue;
        if (reg.user_profile_id !== userProfileId) continue;
        if (reg.trig_status_name === 'cancelled') continue;
        ownRegByEventId.set(reg.event_id, { position: reg.position ?? null });
      }
    }
    return list.map((j: any) => {
      const events = (j.events ?? []) as Array<Record<string, unknown>>;
      const userRegisteredEvents: CalendarRodeoUserEvent[] = [];
      for (const e of events) {
        const eventId = typeof e.id === 'string' ? e.id : '';
        if (!eventId) continue;
        const reg = ownRegByEventId.get(eventId);
        if (!reg) continue;
        const title = getEventDisplayTitle(e as any);
        const label = reg.position ? `${title} — ${reg.position}` : title;
        userRegisteredEvents.push({ eventId, label });
      }
      const resolvedOrgName =
        j.organization_name ??
        (typeof j.primary_organization_id === 'string'
          ? orgNameById.get(j.primary_organization_id)
          : undefined) ??
        null;
      return {
        ...j,
        organization_name: resolvedOrgName,
        ...(userRegisteredEvents.length > 0 ? { userRegisteredEvents } : {}),
      };
    });
  }, [categorized.upcoming, myRegistrations, myOrganizations, userProfileId]);

  // Jackpots the user is personally registered for — drives the red dot
  // in the calendar. Derived from the same transform above so the two
  // signals stay in lockstep.
  const registeredJackpotIds = useMemo(() => {
    const ids = new Set<string>();
    for (const j of calendarJackpots) {
      if (j.userRegisteredEvents && j.userRegisteredEvents.length > 0) {
        ids.add(j.id);
      }
    }
    return ids;
  }, [calendarJackpots]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as TabValue);
    sessionStorage.setItem('jackpots-tab', tab);
  };

  const paginate = (items: any[], tab: TabValue) => {
    const page = pages[tab];
    const start = (page - 1) * PAGE_SIZE;
    return {
      items: items.slice(start, start + PAGE_SIZE),
      totalPages: Math.ceil(items.length / PAGE_SIZE),
      page,
    };
  };

  const setPage = (tab: TabValue, p: number) => {
    setPages((prev) => ({ ...prev, [tab]: p }));
  };

  const renderTab = (tab: TabValue) => {
    const items = categorized[tab];
    const { items: pageItems, totalPages, page } = paginate(items, tab);

    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      );
    }

    if (pageItems.length === 0) {
      return (
        <EmptyState
          icon={Search}
          title={`No ${tab} jackpots`}
          description={
            tab === 'upcoming'
              ? 'Check back soon for new events.'
              : 'No past jackpots found.'
          }
        />
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pageItems.map((j: any) => (
            <JackpotCard
              key={j.id}
              jackpot={j}
              onClick={() => {
                sessionStorage.setItem('jackpots-scroll', String(window.scrollY));
                navigate(`/jackpots/${j.id}`);
              }}
              showPublished={isAdmin}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-xs text-muted-foreground">
              Page {page} of {totalPages} ({items.length} total)
            </p>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(tab, page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(tab, page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {activeTab === 'past' ? 'Past Jackpots' : 'Upcoming Jackpots'}
        </h1>
      </div>

      {!isLoading && showCalendar && (
        <RodeoCalendarView
          rodeos={calendarJackpots}
          registeredRodeoIds={registeredJackpotIds}
          title="Jackpot Calendar"
          itemNoun="jackpot"
        />
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="upcoming" className="gap-1.5">
              Upcoming
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                {categorized.upcoming.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="past" className="gap-1.5">
              Past
              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                {categorized.past.length}
              </Badge>
            </TabsTrigger>
          </TabsList>
          <label
            htmlFor="jackpots-calendar-toggle"
            className="flex items-center gap-2 text-sm text-muted-foreground select-none"
          >
            <Switch
              id="jackpots-calendar-toggle"
              checked={showCalendar}
              onCheckedChange={handleShowCalendarChange}
              aria-label="Show calendar"
            />
            <span>Show calendar</span>
          </label>
        </div>
        <TabsContent value="upcoming" className="mt-4">
          {renderTab('upcoming')}
        </TabsContent>
        <TabsContent value="past" className="mt-4">
          {renderTab('past')}
        </TabsContent>
      </Tabs>
    </div>
  );
}
